// ─────────────────────────────────────────────────────────────────────────────
// React Query hooks for the HR Performance Scores API
// (/api/hr-performance/scores). Server enforces scope + admin-only calculate;
// these hooks are UI plumbing only.
// ─────────────────────────────────────────────────────────────────────────────

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type { PerformanceScore } from '../performanceScoring';

/** Scoped list of scores for a period (admin / department supervisor view). */
export function useScores(period: string) {
  return useQuery({
    queryKey: ['hr', 'scores', period],
    queryFn: async () => {
      const { data } = await api.get(`/api/hr-performance/scores?period=${encodeURIComponent(period)}`);
      return data as { data: PerformanceScore[]; total: number };
    },
    placeholderData: keepPreviousData,
  });
}

/** The signed-in employee's own scores (self-view). */
export function useMyScores() {
  return useQuery({
    queryKey: ['hr', 'my-scores'],
    queryFn: async () => {
      const { data } = await api.get('/api/hr-performance/scores/mine');
      return data as { data: PerformanceScore[] };
    },
  });
}

/** Admin/supervisor action: (re)compute scores for a period. */
export function useCalculateScores() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (period: string) => {
      const { data } = await api.post(`/api/hr-performance/scores/calculate?period=${encodeURIComponent(period)}`);
      return data as { period: string; scored: number; skipped: number };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'scores'] });
      qc.invalidateQueries({ queryKey: ['hr', 'my-scores'] });
    },
  });
}
